<template>
  <div class="m-3" style="height: 92vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <input ref="fileInput" class="hidden" type="file" @change="handleFileUpload" />
      <div class="w-full col-span-12 md:col-span-2">
        <horizontal-button :title="t('selectImportFile')" @click.prevent="openFileDialog()" />
      </div>
      <div class="w-full col-span-12 md:col-span-3">
        <a @click="handleSampleDownload" class="inline-flex items-center text-blue-500 hover:text-blue-700 underline cursor-pointer">
          <span v-if="generatingSample" class="mr-2">
            <svg class="animate-spin -ml-1 h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {{ t('downloadSampleFile') }}
        </a>
      </div>
    </div>

    <!-- Search and Controls -->
    <div
        :class="{ hidden: !rawRowData.length }"
        class="grid grid-cols-12 items-center content-center gap-2 mt-2"
    >
      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-6"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="w-full col-span-12 md:col-span-2">
        <horizontal-button 
          :title="t('deleteSelected')" 
          @click.prevent="deleteSelectedRows"
          :disabled="!selectedRows.length"
          type="danger"
        />
      </div>
      
      <div class="w-full col-span-12 md:col-span-4">
        <horizontal-button 
          :title="isUploading ? t('uploading') : t('uploadFile')" 
          @click.prevent="uploadToServer"
          :type="'primary'"
          :loading="isUploading"
        />
      </div>
    </div>

    <!-- Validation Results -->
    <div v-if="showValidation" class="mt-4 mb-4">
      <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 transition-all duration-300" v-if="validationErrors.length">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-yellow-700 font-medium">{{ t('validationErrorsFound') }}</p>
            <p class="text-yellow-600 text-sm mt-1">{{ t('validationErrorsCount', { count: validationErrors.length }) }}</p>
          </div>
          <button @click="hideValidation" class="text-yellow-600 hover:text-yellow-800">
            <Icon icon="mdi:close" class="text-lg" />
          </button>
        </div>
        
        <!-- Error summary - show the first 5 errors -->
        <ul class="list-disc pl-5 mt-3 max-h-40 overflow-auto">
          <li v-for="(error, index) in validationErrors.slice(0, 5)" :key="index" class="text-yellow-700 mb-1">
            <span class="font-medium">{{ t('row') }} {{ error.row }}:</span> {{ error.message }}
            <button 
              @click="scrollToRow(error.row)" 
              class="ml-2 text-blue-500 hover:text-blue-700 text-sm"
              type="button"
            >
              {{ t('scrollToError') }}
            </button>
          </li>
        </ul>
        
        <!-- Show more errors if there are more than 5 -->
        <div v-if="validationErrors.length > 5" class="mt-2 text-center">
          <button 
            @click="showAllErrors = !showAllErrors" 
            class="text-sm text-yellow-700 hover:text-yellow-900 underline"
            type="button"
          >
            {{ showAllErrors ? t('showLessErrors') : t('showMoreErrors', { count: validationErrors.length - 5 }) }}
          </button>
        </div>
        
        <!-- All errors dialog -->
        <el-dialog 
          v-model="showAllErrors" 
          :title="t('allValidationErrors')"
          width="60%"
        >
          <el-table
            :data="validationErrors"
            height="350"
            style="width: 100%"
          >
            <el-table-column 
              :label="t('row')" 
              prop="row" 
              width="80" 
              sortable
            />
            <el-table-column 
              :label="t('field')" 
              prop="field" 
              width="120" 
              sortable
            />
            <el-table-column 
              :label="t('message')" 
              prop="message" 
            />
            <el-table-column 
              width="100"
              align="center"
            >
              <template #default="scope">
                <button 
                  @click="scrollToRowAndCloseDialog(scope.row.row)" 
                  class="text-blue-500 hover:text-blue-700"
                  type="button"
                >
                  {{ t('goToRow') }}
                </button>
              </template>
            </el-table-column>
          </el-table>
        </el-dialog>
      </div>
      
      <div class="bg-green-50 border-l-4 border-green-400 p-4 transition-all duration-300" v-else>
        <div class="flex justify-between items-start">
          <div>
            <p class="text-green-700">{{ t('allDataValid') }}</p>
            <p class="text-green-600 text-sm mt-1">{{ t('uploadSuccess') }}</p>
          </div>
          <button @click="hideValidation" class="text-green-600 hover:text-green-800">
            <Icon icon="mdi:close" class="text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- Element Plus Table V2 -->
    <div v-if="rawRowData.length" class="mt-4 overflow-auto" style="height: 80vh">
      <!-- Processing indicator -->
      <div v-if="isProcessing" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white p-4 rounded-lg shadow-lg">
        <div class="flex items-center justify-center">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ t('processing') }}...</span>
        </div>
      </div>

      <!-- Table V2 -->
      <el-auto-resizer>
        <template #default="{ height, width }">
                      <el-table-v2
              dir="ltr"
              ref="dataTable"
              :data="filteredData"
              :columns="tableColumns"
              :row-height="40"
              :header-height="80"
              :row-class="getRowClass"
              @selection-change="handleSelectionChange"
              :width="width"
              :height="height"
              fixed
            />
        </template>

      </el-auto-resizer>
      
    </div>
    
    <!-- Empty State -->
    <div v-else class="mt-20 text-center text-gray-500">
      <el-icon class="text-6xl mb-4"><Upload /></el-icon>
      <p>{{ t('selectFileToImport') }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, h } from 'vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'

import { useI18n } from 'vue-i18n'
import { Search, Upload, WarningFilled } from '@element-plus/icons-vue'
import * as XLSX from 'xlsx'
// @ts-ignore
import Papa from 'papaparse'
// @ts-ignore
import * as chardet from 'chardet'
import { ElLoading, ElMessage, ElTableV2, ElSelect, ElOption, ElInput, ElIcon, ElTooltip, ElAutoResizer } from 'element-plus'
import { Icon } from '@iconify/vue'
import Item, { itemI18nMessages } from '@/models/inventory/Item'

export default defineComponent({
  name: 'ImportItems',

  components: {
    HorizontalButton,
    ElTableV2,
    ElSelect,
    ElOption,
    ElInput,
    ElIcon,
    ElTooltip,
    Upload,
    WarningFilled,
    Icon,
    ElAutoResizer
  },

  setup() {
    const messages = {
      en: {
        ...itemI18nMessages.en,
        selectImportFile: 'Select import file',
        uploadFile: 'Upload',
        uploading: 'Uploading',
        uploadSuccessMessage: 'items uploaded successfully',
        uploadFailed: 'Failed to upload items. Please try again.',
        validationErrorsFound: 'Please fix the following errors:',
        validationErrorsCount: '{count} validation errors found',
        noDataToUpload: 'No data to upload. Please select a file first.',
        row: 'Row',
        field: 'Field',
        message: 'Message',
        allDataValid: 'All data is valid and ready to upload',
        uploadSuccess: 'Items have been successfully uploaded',
        hideValidation: 'Hide Validation',
        scrollToError: 'Go to row',
        goToRow: 'Go to row',
        showMoreErrors: 'Show {count} more errors',
        showLessErrors: 'Show fewer errors',
        allValidationErrors: 'All Validation Errors',
        skip: 'Skip this column',
        required: 'Required',
        invalidNumber: 'Must be a valid number',
        invalidPrice: 'Price must be greater than zero',
        invalidQuantity: 'Quantity must be a positive number',
        nameRequired: 'Item name is required',
        codeRequired: 'Item code is required',
        validationPassed: 'Validation passed. Ready to upload.',
        uploadValidData: 'Upload Valid Data',
        selectFileToImport: 'Please select a file to import',
        mappedTo: 'Mapped to',
        operations: 'Operations',
        deleteSelected: 'Delete Selected',
        deleteConfirm: 'Are you sure you want to delete the selected rows?',
        noRowsSelected: 'No rows selected',
        rowsDeleted: 'Selected rows have been deleted',
        rowDeleted: 'Row has been deleted',
        selectMapping: 'Select field mapping',
        // Column names
        name: 'Name',
        type: 'Type',
        parent: 'Parent',
        description: 'Item Description',
        barcode: 'Barcode',
        sku: 'SKU',
        priceType: 'Price Type',
        listPriceAmount: 'List Price',
        salePriceAmount: 'Sale Price',
        cost: 'Cost',
        currentLocationStockQuantity: 'Quantity',
        alertWhenQTYIs: 'Alert When QTY Is',
        receiptName: 'Receipt Name',
        unlimitedStock: 'Unlimited Stock',
        sellOutOfStock: 'Sell Out of Stock',
        discountEligible: 'Discount Eligible',
        serialized: 'Serialized',
        serials: 'Serials',
        attributes: 'Attributes',
        variants: 'Variants',
        customPrices: 'Custom Prices',
        vendor: 'Vendor',
        itemBrand: 'Brand',
        itemCategory: 'Category',
        itemTags: 'Tags',
        // Performance messages
        processingData: 'Processing data...',
        processing: 'Processing',
        validatingData: 'Validating data...',
        uploadingData: 'Uploading data...',
        largeFileWarning: 'Large file detected ({count} rows). Performance may be impacted.',
        downloadSampleFile: 'Download Sample File',
        delete: 'Delete',
        uploadInProgress: 'Upload in progress. Please wait...',
        leavePageConfirm: 'You have unsaved changes that will be lost if you leave this page.\n\nAre you sure you want to leave?',
        fileProcessingInProgress: 'File processing in progress',
        isExpirable: 'Expires',
        batchNumber: 'Batch Number',
        manufactureDate: 'Manufacture Date',
        expiryDate: 'Expiry Date'
      },
      ar: {
        ...itemI18nMessages.ar,
        selectImportFile: 'اختر ملف الاستيراد',
        uploadFile: 'رفع',
        uploading: 'جاري الرفع',
        uploadSuccessMessage: 'تم رفع المنتجات بنجاح',
        uploadFailed: 'فشل في تحميل العناصر. يرجى المحاولة مرة أخرى.',
        validationErrorsFound: 'يرجى إصلاح الأخطاء التالية:',
        validationErrorsCount: 'تم العثور على {count} أخطاء تحقق',
        noDataToUpload: 'لا توجد بيانات للتحميل. يرجى تحديد ملف أولاً.',
        row: 'صف',
        field: 'حقل',
        message: 'رسالة',
        allDataValid: 'جميع البيانات صالحة وجاهزة للتحميل',
        uploadSuccess: 'تم تحميل العناصر بنجاح',
        hideValidation: 'إخفاء التحقق',
        scrollToError: 'انتقل إلى الصف',
        goToRow: 'انتقل إلى الصف',
        showMoreErrors: 'إظهار {count} أخطاء أخرى',
        showLessErrors: 'إظهار أخطاء أقل',
        allValidationErrors: 'جميع أخطاء التحقق',
        skip: 'تخطي هذا العمود',
        required: 'مطلوب',
        invalidNumber: 'يجب أن يكون رقمًا صالحًا',
        invalidPrice: 'يجب أن يكون السعر أكبر من الصفر',
        invalidQuantity: 'يجب أن تكون الكمية رقمًا موجبًا',
        nameRequired: 'اسم المنتج مطلوب',
        codeRequired: 'رمز المنتج مطلوب',
        validationPassed: 'نجح التحقق. جاهز للتحميل.',
        uploadValidData: 'تحميل البيانات الصالحة',
        selectFileToImport: 'الرجاء تحديد ملف للاستيراد',
        mappedTo: 'معين إلى',
        operations: 'العمليات',
        deleteSelected: 'حذف المحدد',
        deleteConfirm: 'هل أنت متأكد أنك تريد حذف الصفوف المحددة؟',
        noRowsSelected: 'لم يتم تحديد أي صفوف',
        rowsDeleted: 'تم حذف الصفوف المحددة',
        rowDeleted: 'تم حذف الصف',
        selectMapping: 'اختر تعيين الحقل',
        // Column names
        name: 'الاسم',
        type: 'النوع',
        parent: 'الأصل',
        description: 'الوصف',
        barcode: 'الباركود',
        sku: 'الرمز التعريفي',
        priceType: 'نوع السعر',
        listPriceAmount: 'سعر القائمة',
        salePriceAmount: 'سعر البيع',
        cost: 'التكلفة',
        currentLocationStockQuantity: 'الكمية',
        alertWhenQTYIs: 'تنبيه عند الكمية',
        receiptName: 'اسم المنتج في الفاتورة',
        unlimitedStock: 'مخزون غير محدود',
        sellOutOfStock: 'بيع نفاد المخزون',
        discountEligible: 'يخضع للخصم',
        serialized: 'مرقم',
        serials: 'الأرقام التسلسلية',
        attributes: 'السمات',
        variants: 'المتغيرات',
        customPrices: 'الأسعار المخصصة',
        vendor: 'المورّد',
        itemBrand: 'العلامة التجارية',
        itemCategory: 'التصنيف',
        itemTags: 'الوسوم',
        // Performance messages
        processingData: 'جاري معالجة البيانات...',
        processing: 'جاري المعالجة',
        validatingData: 'جاري التحقق من البيانات...',
        uploadingData: 'جاري رفع البيانات...',
        largeFileWarning: 'تم اكتشاف ملف كبير ({count} صف). قد يتأثر الأداء.',
        downloadSampleFile: 'تحميل ملف عينة',
        delete: 'حذف',
        uploadInProgress: 'جاري الرفع. يرجى الإنتظار...',
        leavePageConfirm: 'لديك تغييرات غير محفوظة ستفقد إذا غادرت هذه الصفحة.\n\nهل أنت متأكد أنك تريد المغادرة؟',
        fileProcessingInProgress: 'جاري معالجة الملف',
        isExpirable: 'تنتهي صلاحيتة',
        batchNumber: 'رقم الوجبة',
        manufactureDate: 'تاريخ الصنع',
        expiryDate: 'تاريخ الانتهاء'
      }
    }

    const { t } = useI18n({
      messages
    })

    return {
      t,
      Search,
      Upload
    }
  },

  data() {
    return {
      fileColumns: [] as string[],
      rawRowData: [] as any[],
      searchTerm: '',
      selectedRows: [] as any[],
      generatingSample: false,
      
      // Processing
      isProcessing: false,
      isUploading: false,
      
      // Column mapping
      columnMapping: {} as Record<string, string>,
      requiredFields: [
        // Core Item Fields
        { value: 'name', label: 'name', required: true },
        { value: 'receiptName', label: 'receiptName', required: false },
        { value: 'type', label: 'type', required: true },
        { value: 'parent', label: 'parent', required: false },
        { value: 'description', label: 'description' },
        { value: 'barcode', label: 'barcode', required: false },
        { value: 'sku', label: 'sku', required: false },
        
        // Price and Cost Fields
        { value: 'priceType', label: 'priceType', required: true },
        { value: 'listPriceAmount', label: 'listPriceAmount', required: true },
        { value: 'salePriceAmount', label: 'salePriceAmount', required: true },
        { value: 'cost', label: 'cost', required: true },
        
        // Inventory Control Fields
        { value: 'currentLocationStockQuantity', label: 'currentLocationStockQuantity' },
        { value: 'alertWhenQTYIs', label: 'alertWhenQTYIs' },
        { value: 'unlimitedStock', label: 'unlimitedStock', type: 'boolean', required: true },
        { value: 'sellOutOfStock', label: 'sellOutOfStock', type: 'boolean', required: true },
        { value: 'discountEligible', label: 'discountEligible', type: 'boolean', required: true },
        { value: 'serialized', label: 'serialized', type: 'boolean', required: true },
        { value: 'serials', label: 'serials', },
        
        // New Fields
        { value: 'attributes', label: 'attributes', type: 'array' },
        { value: 'variants', label: 'variants', type: 'array' },
        { value: 'customPrices', label: 'customPrices', type: 'array' },
        
        // Category, Vendor, Brand
        { value: 'vendor', label: 'vendor' },
        { value: 'itemBrand', label: 'itemBrand' },
        { value: 'itemCategory', label: 'itemCategory' },
        { value: 'itemTags', label: 'itemTags', type: 'array' },
        
        // Skip option
        { value: 'skip', label: 'skip' },

        // Expirable fields
        { value: 'isExpirable', label: 'isExpirable', type: 'boolean' },
        { value: 'batchNumber', label: 'batchNumber' },
        { value: 'manufactureDate', label: 'manufactureDate' },
        { value: 'expiryDate', label: 'expiryDate' }
      ],
      
      // Validation
      validationErrors: [] as { row: number, field: string, message: string }[],
      showValidation: false,
      
      // Error tracking by row/cell
      rowErrors: {} as Record<number, string[]>,
      cellErrors: {} as Record<string, string>, // format: "rowIndex:columnName"
      showAllErrors: false,
      
      // Upload protection
      beforeUnloadHandler: null as ((e: BeforeUnloadEvent) => void) | null,
      routerBeforeEach: null as (() => void) | null,
      hasFileSelected: false,
      
      // Performance optimization
      cellChangeTimeout: null as number | null,
      errorLookupCache: {} as Record<string, string>
    }
  },

  mounted() {
    // Component initialization
  },

  beforeUnmount() {
    // Clean up event listeners when component is destroyed
    this.removeUploadProtection()
  },

  computed: {
    filteredData() {
      if (!this.searchTerm) {
        return this.rawRowData
      }
      
      const searchLower = this.searchTerm.toLowerCase()
      return this.rawRowData.filter(row => {
        return Object.values(row).some(value => {
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(searchLower)
        })
      })
    },

    tableColumns(): any[] {
      const columns: any[] = []
      
      // Selection column - optimized with better change handling
      columns.push({
        key: 'selection',
        dataKey: 'selection',
        title: '',
        width: 45,
        cellRenderer: ({ rowData, rowIndex }: any) => {
          const isSelected = this.selectedRows.includes(rowData)
          return h('div', { class: 'flex justify-center items-center h-full' }, [
            h('input', {
              type: 'checkbox',
              checked: isSelected,
              style: 'transform: scale(0.9);',
              onChange: (e: any) => {
                // Batch updates for better performance
                this.$nextTick(() => {
                  if (e.target.checked) {
                    if (!isSelected) {
                      this.selectedRows.push(rowData)
                    }
                  } else {
                    const index = this.selectedRows.indexOf(rowData)
                    if (index > -1) {
                      this.selectedRows.splice(index, 1)
                    }
                  }
                })
              }
            })
          ])
        }
      })

      // Row number column - simple and fast
      columns.push({
        key: 'rowNumber',
        dataKey: 'rowNumber',
        title: this.t('row'),
        width: 60,
        cellRenderer: ({ rowIndex }: any) => {
          return h('div', { class: 'text-center text-sm font-medium' }, rowIndex + 1)
        }
      })

      // Dynamic columns based on file data - optimized for performance
      this.fileColumns.forEach((column: string) => {
        const mappedField = this.columnMapping[column]
        const isRequired = this.isFieldRequired(mappedField)
        
        columns.push({
          key: column,
          dataKey: column,
          title: column,
          width: 200,
          headerCellRenderer: () => {
            return h('div', { class: 'flex flex-col w-full px-1 py-0.5' }, [
              h('div', { class: 'font-medium text-xs mb-1 truncate' }, column),
              h('div', { class: 'flex items-center' }, [
                isRequired ? h('span', { class: 'required-field-star mr-0.5 text-xs' }, '*') : null,
                h(ElSelect, {
                  modelValue: mappedField,
                  'onUpdate:modelValue': (value: string) => {
                    this.columnMapping[column] = value
                    // Keep validation visible when mapping changes for better UX
                  },
                  class: 'w-full header-select',
                  size: 'small',
                  placeholder: this.t('selectMapping'),
                  teleported: true, // Use teleported to fix z-index issues
                  popperClass: 'header-select-dropdown',
                  style: 'font-size: 11px; min-height: 24px;'
                }, {
                  default: () => this.requiredFields.map(field => 
                    h(ElOption, {
                      key: field.value,
                      value: field.value,
                      label: this.t(field.label),
                      style: 'font-size: 11px; min-height: 20px;'
                    })
                  )
                })
              ])
            ])
          },
          cellRenderer: ({ rowData, rowIndex }: any) => {
            // Pre-calculate error state to avoid repeated lookups
            const hasError = mappedField !== 'skip' && this.rowErrors[rowIndex]
            const errorMessage = hasError ? this.getCellErrorMessage(rowIndex, column) : null
            
            return h('div', { 
              class: 'relative w-full px-1 py-0.5',
              style: hasError ? 'background-color: #FFF5F5;' : ''
            }, [
              h(ElInput, {
                modelValue: rowData[column] || '',
                'onUpdate:modelValue': (value: string) => {
                  // Debounce updates for better performance
                  rowData[column] = value
                  this.debouncedCellChange()
                },
                class: hasError ? 'error-input' : '',
                size: 'small',
                clearable: true,
                style: 'font-size: 12px;'
              }),
              errorMessage ? h('div', { 
                class: 'absolute -top-0.5 -right-0.5 z-10' 
              }, [
                h(ElTooltip, {
                  content: errorMessage,
                  placement: 'top-end',
                  showAfter: 100
                }, {
                  default: () => h('div', { 
                    class: 'w-3 h-3 bg-red-500 rounded-full flex items-center justify-center text-white cursor-help',
                    style: 'font-size: 8px; line-height: 1;'
                  }, '!')
                })
              ]) : null
            ])
          }
        })
      })

      // Operations column
      columns.push({
        key: 'operations',
        dataKey: 'operations',
        title: this.t('delete'),
        width: 70,
        align: 'center',
        headerCellRenderer: () => {
          return h('div', { class: 'font-medium text-xs' }, this.t('delete'))
        },
        cellRenderer: ({ rowIndex }: any) => {
          return h('button', {
            class: 'delete-btn-compact',
            type: 'button',
            onClick: () => this.deleteRow(rowIndex)
          }, [
            h(Icon, { icon: 'mdi:delete-outline', style: 'font-size: 14px;' })
          ])
        }
      })

      return columns
    }
  },

  methods: {
    tableRowClassName({ row, rowIndex }: { row: any, rowIndex: number }) {
      // Add error class to rows with validation errors
      if (this.rowErrors[rowIndex]) {
        return 'error-row'
      }
      return ''
    },

    getRowClass({ rowIndex }: { rowIndex: number }) {
      // Add error class to rows with validation errors for table-v2
      if (this.rowErrors[rowIndex]) {
        return 'error-row'
      }
      return ''
    },
    
    getCellErrorMessage(rowIndex: number, column: string): string | null {
      const mappedField = this.columnMapping[column]
      
      // If this column isn't mapped to a field, it can't have errors
      if (mappedField === 'skip') return null
      
      // Performance optimization: use Map for faster error lookups
      const errorKey = `${rowIndex + 1}:${mappedField}`
      return this.errorLookupCache[errorKey] || null
    },
    
    handleSelectionChange(selection: any[]) {
      this.selectedRows = selection
    },
    
    deleteSelectedRows() {
      if (this.selectedRows.length === 0) {
        ElMessage.warning(this.t('noRowsSelected'))
        return
      }
      
      // Get indices of selected rows
      const indicesToRemove = this.selectedRows.map(selectedRow => {
        return this.rawRowData.findIndex(row => row === selectedRow)
      }).filter(index => index !== -1)
      
      // Sort in descending order to avoid index shifting when removing items
      indicesToRemove.sort((a, b) => b - a)
      
      // Remove rows from the end to the beginning
      indicesToRemove.forEach(index => {
        this.rawRowData.splice(index, 1)
      })
      
      // Update validation
      this.showValidation = false
      
      ElMessage.success(this.t('rowsDeleted'))
    },

    deleteRow(index: number) {
      this.rawRowData.splice(index, 1)
      
      // Update validation
      this.showValidation = false
      
      ElMessage.success(this.t('rowDeleted'))
    },

    onCellValueChanged() {
      // Keep validation visible when user edits a cell
      // Just trigger reactive update without clearing validation
    },

    debouncedCellChange() {
      // Debounce cell changes to improve performance
      if (this.cellChangeTimeout) {
        clearTimeout(this.cellChangeTimeout)
      }
      this.cellChangeTimeout = setTimeout(() => {
        this.onCellValueChanged()
      }, 150)
    },

    handleFileUpload(event: Event) {
      const loading = ElLoading.service({
        lock: true,
        text: this.t('uploading')
      })

      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) {
        loading.close()
        return
      }

      // Add upload protection when xlsx file is selected
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        this.hasFileSelected = true
        this.addUploadProtection()
      }

      // Reset states
      this.showValidation = false
      this.validationErrors = []
      this.columnMapping = {}
      this.rowErrors = {}
      this.cellErrors = {}
      this.selectedRows = []
      this.rawRowData = [] // Clear existing data

      const reader = new FileReader()
      reader.onload = () => {
        const data = reader.result

        if (file.name.endsWith('.csv')) {
          let d: any = data as ArrayBuffer
          // Detect encoding using chardet
          const encoding = chardet.detect(new Uint8Array(d))
          const decoder = new TextDecoder(encoding!)
          const decodedData = decoder.decode(d)
          // Remove BOM if present
          if (decodedData.charCodeAt(0) === 0xfeff) {
            d = decodedData.slice(1)
          } else {
            d = decodedData
          }
          this.parseCSVData(d)
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          this.parseXLSXData(data as ArrayBuffer)
        } else {
          this.removeUploadProtection() // Clean up protection for unsupported format
          ElMessage.error('Unsupported file format')
        }
        
        loading.close()
      }

      reader.onerror = () => {
        loading.close()
        this.removeUploadProtection() // Clean up protection on error
        ElMessage.error('Failed to read file')
      }

      reader.readAsArrayBuffer(file)
    },
    
    parseCSVData(data: string) {
      // Remove BOM if present
      if (data.charCodeAt(0) === 0xfeff) {
        data = data.slice(1)
      }
      
      const loading = ElLoading.service({
        lock: true,
        text: this.t('processingData')
      })
      
      // Parse in a timeout to allow UI to render loading indicator
      setTimeout(() => {
        Papa.parse(data, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          encoding: 'utf-8',
          complete: (results: any) => {
            loading.close()
            
            if (results.data && results.data.length > 0) {
              this.rawRowData = results.data
              this.fileColumns = Object.keys(results.data[0])
              
              // Initialize column mapping
              this.fileColumns.forEach(col => {
                this.columnMapping[col] = 'skip'
              })
              
              // Auto-map columns
              this.autoMapColumns()
              
              if (this.rawRowData.length > 500) {
                ElMessage.warning(this.t('largeFileWarning', { count: this.rawRowData.length }))
              }
            }
          },
          error: (error: any) => {
            loading.close()
            ElMessage.error('Error parsing CSV')
            console.error('Error parsing CSV:', error)
          }
        })
      }, 100)
    },

    parseXLSXData(data: ArrayBuffer) {
      const loading = ElLoading.service({
        lock: true,
        text: this.t('processingData')
      })
      
      // Process in a timeout to allow UI to render loading indicator
      setTimeout(() => {
        try {
          const workbook = XLSX.read(data, { 
            type: 'array',
            raw: false, // Convert numbers to strings to preserve formatting
            cellDates: true, // Parse dates
            cellNF: true, // Preserve number formatting
            cellText: true // Preserve text formatting
          })
          const sheetName = workbook.SheetNames[0]
          const sheet = workbook.Sheets[sheetName]
          
          // Get the range of the sheet
          const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1')
          
          // Get all headers from the first row
          const headers: string[] = []
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: C })]
            if (cell && cell.v) {
              headers.push(String(cell.v).trim())
            }
          }
          
          // Convert to JSON with all headers, skipping the header row
          const jsonData = XLSX.utils.sheet_to_json(sheet, {
            header: headers,
            raw: false, // Convert numbers to strings
            defval: '', // Default value for empty cells
            blankrows: false, // Skip blank rows
            range: { // Skip the header row
              s: { r: range.s.r + 1, c: range.s.c },
              e: { r: range.e.r, c: range.e.c }
            }
          })
          
          this.rawRowData = jsonData
          
          if (jsonData.length > 0) {
            this.fileColumns = headers
            
            // Initialize column mapping with default "skip"
            this.fileColumns.forEach(col => {
              this.columnMapping[col] = 'skip'
            })
            
            // Auto-map columns based on header names
            this.autoMapColumns()
          }
          
          loading.close()
          
          if (this.rawRowData.length > 500) {
            ElMessage.warning(this.t('largeFileWarning', { count: this.rawRowData.length }))
          }
        } catch (error) {
          loading.close()
          ElMessage.error('Error parsing Excel file')
          console.error('Error parsing Excel file:', error)
        }
      }, 100)
    },
    
    autoMapColumns() {
      // Try to automatically map columns based on their names
      this.fileColumns.forEach(column => {
        const colLower = column.toLowerCase()
        
        // Map name fields
        if (colLower == 'name' || colLower == 'item name') {
          this.columnMapping[column] = 'name'
        } 
        // Map type fields
        else if (colLower == 'type') {
          this.columnMapping[column] = 'type'
        }
        // Map parent fields
        else if (colLower == 'parent') {
          this.columnMapping[column] = 'parent'
        }
        // Map description fields
        else if (colLower == 'item description') {
          this.columnMapping[column] = 'description'
        }
        // Map barcode fields
        else if (colLower == 'barcode') {
          this.columnMapping[column] = 'barcode'
        }
        // Map SKU fields
        else if (colLower == 'sku') {
          this.columnMapping[column] = 'sku'
        }
        // Map price type fields
        else if (colLower == 'price type') {
          this.columnMapping[column] = 'priceType'
        }
        // Map price fields
        else if (colLower == 'list price') {
          this.columnMapping[column] = 'listPriceAmount'
        }
        // Map sale price fields
        else if (colLower == 'sale price' || colLower == 'price') {
          this.columnMapping[column] = 'salePriceAmount'
        }
        // Map cost fields
        else if (colLower == 'cost') {
          this.columnMapping[column] = 'cost'
        }
        // Map quantity fields
        else if (colLower == 'quantity') {
          this.columnMapping[column] = 'currentLocationStockQuantity'
        }
        // Map receipt name fields
        else if (colLower == 'receipt name') {
          this.columnMapping[column] = 'receiptName'
        }
        // Map alert when qty is fields
        else if (colLower == 'alert when qty is') {
          this.columnMapping[column] = 'alertWhenQTYIs'
        }
        // Map attributes fields
        else if (colLower == 'attributes') {
          this.columnMapping[column] = 'attributes'
        }
        // Map variants fields
        else if (colLower == 'variants') {
          this.columnMapping[column] = 'variants'
        }
        // Map serials fields
        else if (colLower == 'serials') {
          this.columnMapping[column] = 'serials'
        }
        // Map custom prices fields
        else if (colLower == 'custom prices') {
          this.columnMapping[column] = 'customPrices'
        }
        // Map category fields
        else if (colLower == 'category') {
          this.columnMapping[column] = 'itemCategory'
        }
        // Map brand fields
        else if (colLower == 'brand') {
          this.columnMapping[column] = 'itemBrand'
        }
        // Map vendor fields
        else if (colLower == 'vendor') {
          this.columnMapping[column] = 'vendor'
        }
        // Map tags fields
        else if (colLower == 'tags') {
          this.columnMapping[column] = 'itemTags'
        }
        // Map boolean fields
        else if (colLower == 'unlimited stock') {
          this.columnMapping[column] = 'unlimitedStock'
        }
        else if (colLower == 'sell out of stock') {
          this.columnMapping[column] = 'sellOutOfStock'
        }
        else if (colLower == 'discount eligible') {
          this.columnMapping[column] = 'discountEligible'
        }
        else if (colLower == 'serialized') {
          this.columnMapping[column] = 'serialized'
        }
        else if (colLower == 'expires') {
          this.columnMapping[column] = 'isExpirable'
        }
        else if (colLower == 'batch no') {
          this.columnMapping[column] = 'batchNumber'
        }
        else if (colLower == 'manufacture date') {
          this.columnMapping[column] = 'manufactureDate'
        }
        else if (colLower == 'expiry date') {
          this.columnMapping[column] = 'expiryDate'
        }
      })
    },
    
    openFileDialog() {
      /* @ts-ignore */
      this.$refs.fileInput.click()
    },

    uploadToServer() {
      if (!this.rawRowData.length) {
        ElMessage.warning(this.t('noDataToUpload'))
        return
      }
      
      this.isUploading = true
      this.addUploadProtection() // Add protection when upload starts
      
      const loading = ElLoading.service({
        lock: true,
        text: this.t('uploadingData')
      })
      
      // Prepare all data at once
      setTimeout(() => {
        this.prepareAndUploadAllData(loading)
      }, 100)
    },
    
    prepareAndUploadAllData(loadingInstance: any) {
      const dataToUpload = []
      
      // Prepare all data at once
      for (let i = 0; i < this.rawRowData.length; i++) {
        const row = this.rawRowData[i]
        const mappedRow: Record<string, any> = { rowIndex: i + 1 }
        
        Object.entries(this.columnMapping).forEach(([srcCol, targetField]) => {
          if (targetField !== 'skip') {
            // Find field definition to get type
            const fieldDef = this.requiredFields.find(f => f.value === targetField)
          
            mappedRow[targetField] = row[srcCol]
          }
        })
        
        dataToUpload.push(mappedRow)
      }
      
      // Send all data to server
      this.sendDataToServer(dataToUpload, loadingInstance)
    },
    
    sendDataToServer(data: any[], loadingInstance: any) {
      // Format data for the API according to the specified format
      const formattedData = data.map(item => {
        return {
          rowNumber: item.rowIndex,
          name: item.name,
          type: item.type.toLowerCase(),
          parent: item.parent,
          description: item.description,
          barcode: item.barcode,
          sku: item.sku,
          priceType: item.priceType.toLowerCase(),
          // Convert dollar amounts to cents for API and handle commas
          listPriceAmount: item.listPriceAmount ? Number(item.listPriceAmount.toString().replace(/,/g, '')) : undefined,
          salePriceAmount: item.salePriceAmount ? Number(item.salePriceAmount.toString().replace(/,/g, '')) : undefined,
          cost: item.cost ? Number(item.cost.toString().replace(/,/g, '')) : undefined,
          currentLocationStockQuantity: item.currentLocationStockQuantity !== undefined ? 
            Number(item.currentLocationStockQuantity.toString().replace(/,/g, '')) : undefined,
          alertWhenQTYIs: item.alertWhenQTYIs ? Number(item.alertWhenQTYIs.toString().replace(/,/g, '')) : undefined,
          receiptName: item.receiptName,
          // Boolean fields
          serialized: this.parseBooleanValue(item.serialized),
          unlimitedStock: this.parseBooleanValue(item.unlimitedStock),
          sellOutOfStock: this.parseBooleanValue(item.sellOutOfStock),
          discountEligible: this.parseBooleanValue(item.discountEligible),
          vendor: item.vendor,
          itemBrand: item.itemBrand,
          itemCategory: item.itemCategory,
          itemTags: item.itemTags,
          attributes: item.attributes,
          variants: item.variants,
          serials: item.serials,
          customPrices: item.customPrices,
          isExpirable: this.parseBooleanValue(item.isExpirable),
          batchNumber: item.batchNumber,
          manufactureDate: item.manufactureDate,
          expiryDate: item.expiryDate
        }
      })

      // Call the API endpoint using this.$http
      interface ImportResponse {
        success: boolean;
        message: string;
        importedCount: number;
        errors?: Array<{row: number, field: string, message: string}>;
      }

      this.$http.post<ImportResponse>('/inventory/items/import', { items: formattedData })
        .then(response => {
          loadingInstance.close()
          this.isUploading = false
          
          if (response.success) {
            // Successfully imported
            this.removeUploadProtection() // Remove protection when upload completes
            ElMessage.success(`${this.t('uploadSuccessMessage')}: ${response.importedCount}`)
            this.showSuccessValidation()
            this.navigateToItems()
          } else {
            // Handle validation errors from server
            this.handleServerValidationErrors(response.errors || [])
          }
        })
        .catch(error => {
          loadingInstance.close()
          this.isUploading = false
          // this.removeUploadProtection() // Remove protection when upload fails
          console.error('Error uploading items:', error)
          ElMessage.error(this.t('uploadFailed'))
        })
    },
    
    showSuccessValidation() {
      this.validationErrors = []
      this.rowErrors = {}
      this.cellErrors = {}
      this.showValidation = true
    },
    
    handleServerValidationErrors(errors: Array<{row: number, field: string, message: string}>) {
      if (!errors || !errors.length) return
      
      // Clear previous validation
      this.validationErrors = []
      this.rowErrors = {}
      this.cellErrors = {}
      this.errorLookupCache = {}
      
      // Process server validation errors
      errors.forEach(error => {
        // Add to validation errors
        this.validationErrors.push({
          row: error.row,
          field: error.field,
          message: error.message
        })
        
        // Mark row as having error
        if (!this.rowErrors[error.row - 1]) {
          this.rowErrors[error.row - 1] = []
        }
        this.rowErrors[error.row - 1].push(error.message)
        
        // Store cell error
        this.storeFieldError(error.row - 1, error.field, error.message)
        
        // Performance optimization: populate error lookup cache
        const errorKey = `${error.row}:${error.field}`
        this.errorLookupCache[errorKey] = error.message
      })
      
      this.showValidation = true
      
      ElMessage.error(this.t('validationErrorsFound'))
    },

    handleSampleDownload() {
      // Check if sample file exists
      this.generatingSample = true;
      
      fetch('/samples/sample-inventory.xlsx', { method: 'HEAD' })
        .then(response => {
          if (response.ok) {
            // File exists, trigger download
            window.location.href = '/samples/sample-inventory.xlsx';
            setTimeout(() => {
              this.generatingSample = false;
            }, 1000);
          }
        })
    },
    
    hideValidation() {
      this.showValidation = false
      this.showAllErrors = false
      // Clear error cache for better performance
      this.errorLookupCache = {}
    },

    scrollToRow(row: number) {
      // Find the table element (el-table-v2)
      const table = this.$refs.dataTable as any
      if (!table) {
        console.warn('Table ref not found')
        return
      }

      // Row numbers are 1-based, but array is 0-based
      const rowIndex = row - 1
      if (rowIndex < 0 || rowIndex >= this.filteredData.length) {
        console.warn('Row index out of bounds:', rowIndex, 'Max:', this.filteredData.length - 1)
        return
      }

      console.log('Scrolling to row:', row, 'index:', rowIndex)
      
      // Debug the table structure (remove this in production)
      this.debugTableStructure()

      // For el-table-v2, the correct method according to Element Plus docs
      try {
        // Method 1: Use scrollTo with row index (el-table-v2 specific)
        if (typeof table.scrollTo === 'function') {
          console.log('Using table.scrollTo with row index')
          table.scrollTo({ scrollTop: rowIndex * 40 })
          return
        }

        // Method 2: Access the internal virtual list directly
        const virtualList = table.$.refs?.windowRef || table.windowRef
        if (virtualList) {
          console.log('Using virtual list scroll')
          if (typeof virtualList.scrollToItem === 'function') {
            virtualList.scrollToItem(rowIndex, 'center')
            return
          } else if (typeof virtualList.scrollTo === 'function') {
            virtualList.scrollTo({ scrollTop: rowIndex * 40 })
            return
          }
        }

        // Method 3: Try to find the scrollable element in DOM
        const tableElement = table.$el
        if (tableElement) {
          console.log('Searching for scrollable element in DOM')
          
          // Try different possible scroll containers
          const containers = [
            tableElement.querySelector('.el-table-v2__body'),
            tableElement.querySelector('.el-virtual-scrollbar__wrap'),
            tableElement.querySelector('.el-scrollbar__wrap'),
            tableElement.querySelector('.el-scrollbar__view'),
            tableElement.querySelector('[data-key="body"]'),
            tableElement.querySelector('.el-table-v2__main')
          ]
          
          for (const container of containers) {
            if (container) {
              console.log('Found scroll container:', container.className)
              const targetScrollTop = rowIndex * 40
              
              // Try both scrollTop property and scrollTo method
              container.scrollTop = targetScrollTop
              
              if (typeof container.scrollTo === 'function') {
                container.scrollTo({
                  top: targetScrollTop,
                  behavior: 'smooth'
                })
              }
              return
            }
          }
        }

        // Method 4: Last resort - try the auto-resizer parent
        const autoResizer = table.$parent?.$el
        if (autoResizer) {
          console.log('Trying auto-resizer scroll')
          const scrollArea = autoResizer.querySelector('.el-table-v2')
          if (scrollArea) {
            scrollArea.scrollTop = rowIndex * 40
          }
        }

        console.warn('No suitable scroll method found - table may not be scrollable')
      } catch (error) {
        console.error('Error in scrollToRow:', error)
      }
    },

    // Debug method to inspect table structure
    debugTableStructure() {
      const table = this.$refs.dataTable as any
      if (!table) {
        console.log('No table ref found')
        return
      }
      
      console.log('Table object:', table)
      console.log('Table methods:', Object.getOwnPropertyNames(table).filter(prop => typeof table[prop] === 'function'))
      console.log('Table $el:', table.$el)
      console.log('Table $refs:', table.$refs)
      console.log('Table windowRef:', table.windowRef)
      console.log('Table virtualListRef:', table.virtualListRef)
      
      if (table.$el) {
        console.log('Available scroll containers:')
        console.log('- .el-table-v2__body:', table.$el.querySelector('.el-table-v2__body'))
        console.log('- .el-virtual-scrollbar__wrap:', table.$el.querySelector('.el-virtual-scrollbar__wrap'))
        console.log('- .el-scrollbar__wrap:', table.$el.querySelector('.el-scrollbar__wrap'))
        console.log('- [role="grid"]:', table.$el.querySelector('[role="grid"]'))
      }
    },

    scrollToRowAndCloseDialog(row: number) {
      this.showAllErrors = false
      setTimeout(() => {
        this.scrollToRow(row)
      }, 300) // Give the dialog time to close
    },

    storeFieldError(rowIndex: number, field: string, message: string) {
      // Find the column(s) that map to this field
      Object.entries(this.columnMapping).forEach(([column, mappedField]) => {
        if (mappedField === field) {
          this.cellErrors[`${rowIndex}:${column}`] = message
        }
      })
    },

    isFieldRequired(field: string): boolean {
      const fieldDef = this.requiredFields.find(f => f.value === field)
      return fieldDef ? fieldDef.required === true : false
    },

    parseBooleanValue(value: string | boolean | undefined): boolean | null {
      if (value === 'true' || value === 'yes') return true
      if (value === 'false' || value === 'no') return false
      return null
    },

    addUploadProtection() {
      // Prevent page refresh/close during file processing or upload
      const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
        if (this.hasFileSelected || this.isUploading) {
          e.preventDefault()
          const message = this.t('fileProcessingInProgress')
          e.returnValue = message
          return message
        }
      }

      // Store the handler for cleanup
      this.beforeUnloadHandler = beforeUnloadHandler
      window.addEventListener('beforeunload', beforeUnloadHandler)

      // Prevent Vue Router navigation during file processing or upload with native confirmation
      if (this.routerBeforeEach) {
        this.routerBeforeEach() // Remove existing guard first
      }
      
      this.routerBeforeEach = this.$router.beforeEach((to, from, next) => {
        if (this.hasFileSelected || this.isUploading) {
          const confirmNavigation = confirm(this.t('leavePageConfirm'))
          if (confirmNavigation) {
            // Temporarily disable the guard to allow navigation
            this.hasFileSelected = false
            this.isUploading = false
            next()
            // Re-enable if needed (shouldn't be needed after navigation)
          } else {
            next(false) // Stay on current page
          }
        } else {
          next()
        }
      })
    },

    removeUploadProtection() {
      // Reset file selected state
      this.hasFileSelected = false
      
      // Remove beforeunload event listener
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler)
        this.beforeUnloadHandler = null
      }

      // Remove router guard
      if (this.routerBeforeEach) {
        this.routerBeforeEach()
        this.routerBeforeEach = null
      }
    },

    navigateToItems() {
      // Remove upload protection before navigation
      this.removeUploadProtection()
      // Navigate to items page after successful upload
      this.$router.push('/inventory/items')
    }
  }
})
</script>

<style>
.el-table .error-row {
  background-color: #FFECEC;
}

.el-table .error-row:hover td {
  background-color: #FFE0E0 !important;
}

.error-input .el-input__inner {
  border-color: #F56C6C !important;
  background-color: #FFF5F5 !important;
}

.delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #F56C6C;
  font-size: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background-color: rgba(245, 108, 108, 0.1);
}

.delete-btn-compact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #F56C6C;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.delete-btn-compact:hover {
  background-color: rgba(245, 108, 108, 0.1);
}

/* Make table headers taller to fit mapping controls */
.m-3 .el-table th {
  padding: 12px 0;
  height: auto;
  background-color: #DEE9FC !important;
}

/* Custom styling for the headers with mapping controls */
.m-3 .el-table .cell {
  overflow: visible;
  white-space: normal;
}

.import-items .m-3 .el-select__wrapper {
  background-color: #DEE9FC !important;
}

/* El-table-v2 header styling to match original */
.m-3 .el-table-v2__header-row {
  background-color: #DEE9FC !important;
  position: relative;
  z-index: 5;
}

.m-3 .el-table-v2__header-cell {
  background-color: #DEE9FC !important;
  border-bottom: 1px solid #EBEEF5;
  position: relative;
  z-index: 5;
}

/* Keep header content styling */
.m-3 .el-table-v2__header-cell .el-select__wrapper {
  background-color: #DEE9FC !important;
}

/* Fix z-index for header select dropdowns */
.m-3 .header-select .el-select__wrapper {
  background-color: #DEE9FC !important;
  position: relative;
  z-index: 10;
}

/* Dropdown menu z-index fix */
.header-select-dropdown {
  z-index: 9999 !important;
}

.header-select-dropdown .el-select-dropdown__item {
  font-size: 11px !important;
  min-height: 20px !important;
  line-height: 20px !important;
  padding: 2px 8px !important;
}

/* Highlight row animation */
.highlight-row {
  background-color: rgba(64, 158, 255, 0.2) !important;
  transition: background-color 0.5s ease-in-out;
}

/* Error summary styles */
.error-summary {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 10px;
}

/* Required field indicator */
.required-field-star {
  color: #F56C6C;
  font-weight: bold;
  font-size: 14px;
}
</style>
